import React, { useEffect, useState } from "react";
import { t } from "i18next";
import { Checkbox, Flex, Form, Modal, Select } from 'antd';
import { api, HydraData } from "../../api/apiProvider";
import urls from "../../api/urls";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import Roles from "../../roles/Roles";
import { DefaultOptionType } from "antd/es/select";
import useSWR from "swr";
import ButtonBlack from "../../components/ui/buttons/ButtonBlack";

const copyFull = async (briefId: number, kpiId: number | null, copyStages: boolean) => {
  return await api.post(`${urls.briefs}/copy_full`, {
    arg: {
      briefId,
      kpiId,
      copyStages,
    }
  });
};

interface Model {
  id: string;
  '@id': string;
  name: string;
}

interface CopyBriefFormProps {
  briefId: number;
  title: string;
  requireKpi?: boolean;
}

export function CopyBriefForm({ briefId, title, requireKpi = true }: CopyBriefFormProps) {
  const navigate = useNavigate();
  const [kpiOptions, setKpiOptions] = useState<DefaultOptionType[]>([]);
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDataSaving, setIsDataSaving] = useState<boolean>(false);

  const getKpisParamsString = () => {
    const params = new URLSearchParams([['pagination', 'false']]);
    return params.toString();
  };

  const { data: kpis } = useSWR<HydraData>(
    `${urls.kpis}?${getKpisParamsString()}`,
    api.get,
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    if (kpis?.data) {
      setKpiOptions(
        kpis.data.map((kpi: Model) => ({
          value: kpi.id,
          label: kpi.name,
        }))
      );
    }
  }, [kpis]);

  const onFinish = async (values: any) => {
    if (!Roles.hasAccess('canCopyBrief')) {
      return;
    }

    setIsDataSaving(true);

    const kpiId = values.kpi?.value ?? undefined;
    const copyStages = values.copyStages ?? false;

    if (requireKpi && !kpiId) {
      setIsDataSaving(false);
      return;
    }

    copyFull(briefId, kpiId, copyStages)
      .then((response) => {
        if (response.id) {
          navigate(`/briefs/${response.id}/description`);
        }
      })
      .catch((errors) => {
        if (errors['hydra:description']) {
          message.error(errors['hydra:description']);
        }
      })
      .finally(() => {
        setIsDataSaving(false);
      });
  };

  return (
    <>
      <a
        onClick={(e) => {
          e.stopPropagation();
          setIsModalOpen(true);
        }}
      >
        {title}
      </a>

      <Modal
        title={title}
        open={isModalOpen}
        onCancel={(e) => {
          e.stopPropagation();
          setIsModalOpen(false);
        }}
        footer={null}
      >
        <Form
          form={form}
          onFinish={onFinish}
          disabled={isDataSaving}
          initialValues={{
            copyStages: true,
            kpi: undefined,
          }}
        >
          <Flex vertical gap="large">
            {requireKpi && (
              <Form.Item
                name="kpi"
                label={t('brief.kpi')}
                rules={[{ required: true, message: t('common.choose') }]}
              >
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  labelInValue
                  placeholder={t('common.choose')}
                  optionFilterProp="children"
                  filterOption={false}
                  options={kpiOptions}
                />
              </Form.Item>
            )}

            <Form.Item name="copyStages" valuePropName="checked">
              <Checkbox>{t('list.copyStages')}</Checkbox>
            </Form.Item>

            <Flex justify="flex-end">
              <Form.Item>
                <ButtonBlack
                  onClick={() => form.submit()}
                  loading={isDataSaving}
                >
                  {t('common.save')}
                </ButtonBlack>
              </Form.Item>
            </Flex>
          </Flex>
        </Form>
      </Modal>
    </>
  );
}
